import { type ReactNode } from "react";
import CountUp from "react-countup";
import { ArrowDownRight, ArrowUpRight, Minus } from "lucide-react";
import { Skeleton } from "@/components/ui/skeleton";
import { cn } from "@/utils/cn";
import { NoAccessDash } from "./state";
import {
  TONE_FILL,
  TONE_TEXT,
  type TWidgetTone,
  useAnimationsEnabled,
} from "./tone";

/**
 * Figures: the headline number and the small stats around it.
 *
 * Part of the widget primitive vocabulary — see ./index.ts. Widgets differ in
 * what they measure, not in how a number, a chart or a person-row looks, so
 * every shape lives here rather than being re-invented per widget.
 */

/**
 * The headline number.
 *
 * Counts up on first paint, which does real work here: several of these tiles
 * show a count that is 0 until data lands, and a number that rolls to its value
 * makes "this just loaded" unmistakable where a number that pops in is
 * indistinguishable from a number that was always there. `tabular-nums` keeps the
 * width steady while it rolls.
 */
export const MetricValue = ({
  value,
  isLoading,
  noAccess,
  scale = "lg",
  tone = "primary",
}: {
  value: number;
  isLoading?: boolean;
  /**
   * The figure could not be read — the query behind it came back 403. Renders a
   * dash rather than the number, because the alternative is `?? 0`, and a zero
   * a reader cannot distinguish from a real zero is worse than no figure at all.
   */
  noAccess?: boolean;
  scale?: "sm" | "md" | "lg" | "xl";
  tone?: TWidgetTone;
}) => {
  const animate = useAnimationsEnabled();

  // Deliberately large. On a home-screen widget the figure is the object; the
  // label explains it afterwards, rather than the two sharing billing. `sm`
  // is the one exception — it exists only for the `xs` card, where 28px is
  // what the 104px row has left once the shared chip-and-title header has taken
  // its 44px and the body its bottom padding. It held at 28 when that header
  // came back: the slack it was eating was air below the figure, not the figure
  // itself, so the number did not have to shrink to make room for the name.
  const sizeClass = {
    sm: "text-[28px] leading-none",
    md: "text-[26px] leading-none",
    lg: "text-[34px] leading-none",
    xl: "text-[42px] leading-[0.95]",
  }[scale];

  const skeletonSizeClass = {
    sm: "h-8 w-14",
    md: "h-8 w-16",
    lg: "h-8 w-16",
    xl: "h-10 w-20",
  }[scale];

  // Before the loading check: a forbidden query is not loading, and a figure
  // that sat on a skeleton forever would read as a hang rather than a refusal.
  if (noAccess) {
    return (
      <p
        className={cn(
          "font-bold tracking-[-0.02em] text-muted-foreground",
          sizeClass,
        )}
      >
        <NoAccessDash />
      </p>
    );
  }

  if (isLoading) {
    return <Skeleton className={cn("rounded-lg", skeletonSizeClass)} />;
  }

  return (
    <p
      className={cn(
        "font-bold tracking-[-0.02em] tabular-nums",
        sizeClass,
        TONE_TEXT[tone],
      )}
    >
      {animate ? (
        <CountUp
          end={value}
          duration={0.9}
          separator=","
          useEasing
          preserveValue
        />
      ) : (
        value.toLocaleString()
      )}
    </p>
  );
};

/**
 * Month-over-month movement. Direction carries the colour, so an increase in
 * headcount and an increase in pending approvals can be tinted differently —
 * `invert` flips which direction counts as good.
 */
export const DeltaPill = ({
  percent,
  label,
  invert = false,
}: {
  percent: number | null | undefined;
  /** Omitted on the `xs` card, where there is no width for "vs last month". */
  label?: string;
  invert?: boolean;
}) => {
  if (percent === null || percent === undefined) return null;

  const isUp = percent > 0;
  const isFlat = percent === 0;
  const isGood = invert ? !isUp : isUp;
  const Icon = isFlat ? Minus : isUp ? ArrowUpRight : ArrowDownRight;

  return (
    <span
      className={cn(
        // Solid tone fill rather than the muted-on-muted pairing used
        // elsewhere: this pill is a movement signal read at a glance across a
        // whole canvas, closer to a phone widget's saturated status chip than
        // to a page's soft status badge.
        "inline-flex items-center gap-0.5 rounded-full px-1.5 py-0.5 text-[10px] font-bold tabular-nums",
        isFlat && "bg-muted text-muted-foreground",
        !isFlat && isGood && "bg-success text-success-foreground",
        !isFlat && !isGood && "bg-danger text-danger-foreground",
      )}
    >
      <Icon size={10} aria-hidden />
      {isUp ? "+" : ""}
      {percent}%
      {label && <span className="font-medium opacity-75">{label}</span>}
    </span>
  );
};

/**
 * The `xs` card's body: the figure, and optionally one signal beside it.
 *
 * A full-width baseline row rather than a centered stack. The mini card anchors
 * its content bottom-left under a floating corner badge, so this fills the
 * width it is given and lets an accessory sit out at the right margin —
 * balancing the badge diagonally instead of leaving the whole top-right corner
 * empty, which is what the centered version did.
 *
 * `trailing` is deliberately a slot rather than a `delta` prop: only some of
 * these widgets have a genuine second figure at this size, and the ones that do
 * express it differently (a percentage, a count). A slot lets each pass what it
 * actually has and the rest pass nothing, without this component pretending to
 * know which shape is right.
 */
export const MiniMetric = ({
  value,
  isLoading,
  noAccess,
  tone = "primary",
  trailing,
}: {
  value: number;
  isLoading?: boolean;
  /** See `MetricValue` — renders a dash instead of a figure that is not readable. */
  noAccess?: boolean;
  tone?: TWidgetTone;
  trailing?: ReactNode;
}) => (
  <div className="flex w-full items-baseline justify-between gap-1.5">
    {/* `min-w-0` on the figure and `shrink-0` on the accessory: if a five-digit
        count ever meets a wide accessory, the accessory keeps its shape and the
        figure truncates rather than both being squeezed into illegibility. */}
    <span className="min-w-0">
      <MetricValue
        value={value}
        isLoading={isLoading}
        noAccess={noAccess}
        tone={tone}
        scale="sm"
      />
    </span>
    {trailing && <span className="shrink-0">{trailing}</span>}
  </div>
);

/** One label/value pair in a widget's secondary row. */
export const MiniStat = ({
  label,
  value,
  tone = "primary",
  isLoading,
  noAccess,
}: {
  label: string;
  value: number;
  tone?: TWidgetTone;
  isLoading?: boolean;
  /** See `MetricValue` — renders a dash instead of a figure that is not readable. */
  noAccess?: boolean;
}) => (
  <div className="min-w-0">
    <div className="flex items-center gap-1.5">
      <span className={cn("size-2 shrink-0 rounded-full", TONE_FILL[tone])} />
      <p className="truncate text-[10px] font-medium tracking-[0.05em] text-muted-foreground uppercase">
        {label}
      </p>
    </div>
    {noAccess ? (
      <p className="mt-0.5 text-sm font-bold text-muted-foreground">
        <NoAccessDash />
      </p>
    ) : isLoading ? (
      <Skeleton className="mt-1 h-4 w-8 rounded" />
    ) : (
      <p className="mt-0.5 text-sm font-bold tabular-nums text-foreground">
        {value.toLocaleString()}
      </p>
    )}
  </div>
);
